/**
 * Unified Answer Protocol v2.0
 *
 * Standardized answer payload and validation result format across all question types.
 *
 * Moved from src/utils/answerProtocol.ts in R31 (DDD Fase 4).
 * Intra-domain import: ./answerPayload (was ../answerPayload.schema)
 */

import { QuestionType } from '../../content'
import { AnswerPayloadSchema } from './answerPayload'

// ============================================================
// ANSWER PAYLOAD — What the renderer produces
// ============================================================

/** Choice selection (multiple choice, bar chart, line chart) */
export interface ChoicePayload {
  kind: 'choice'
  selectedIndex: number
}

/** Text input (fill-in, column arithmetic) */
export interface TextPayload {
  kind: 'text'
  value: string
}

/** Mapping pairs (matching questions) */
export interface MappingPayload {
  kind: 'mapping'
  pairs: [number, number][] // [leftIndex, rightIndex]
}

/** Ordered sequence (ordering questions) */
export interface OrderingPayload {
  kind: 'ordering'
  sequence: number[] // Ordered indices
}

/** Cell selection (schedule reading, table reading) */
export interface CellSelectionPayload {
  kind: 'cell_selection'
  row: number
  col: number
}

/** Key-value map (ratio table, drag-drop-fill) */
export interface KeyValuePayload {
  kind: 'key_value'
  values: Record<string, string>
}

/** Segment selection (text highlight) */
export interface SegmentPayload {
  kind: 'segment'
  segmentId: string
}

/** Sequence of values (number sequence) */
export interface ValueSequencePayload {
  kind: 'value_sequence'
  values: string[]
}

/** Visual data point selection (generalized for charts/schedules) */
export interface VisualDataPayload {
  kind: 'visual_data'
  referenceId: string
  rawIndex?: number
}

/**
 * Two-step choice (TAA Q4 idiom_literal_judge `two_step_judge` MC mode).
 * step1 = index in step1.options (e.g. 0 = letterlijk, 1 = figuurlijk).
 * step2 = optional, only present when step1 triggered the second step.
 */
export interface TwoStepChoicePayload {
  kind: 'two_step_choice'
  step1: number
  step2?: number
}

/** NEW: Time input (clock questions) - MeesterJay 4.1 */
export interface TimePayload {
  kind: 'time'
  value: {
    hours: number
    minutes: number
    format: '24h'
  }
}

/**
 * CANONICAL: Token set (money questions) - MeesterJay 4.1
 * This is the authoritative format for money questions
 */
export interface TokenSetPayload {
  kind: 'token_set'
  value: {
    tokens: Array<{
      valueCents: number
      count: number
    }>
    totalCents: number
  }
}

/** Discriminated union of all answer payload types */
export type AnswerPayload =
  | ChoicePayload
  | TextPayload
  | TokenSetPayload
  | MappingPayload
  | OrderingPayload
  | CellSelectionPayload
  | KeyValuePayload
  | SegmentPayload
  | ValueSequencePayload
  | VisualDataPayload
  | TimePayload
  | TwoStepChoicePayload

// ============================================================
// VALIDATION RESULT — What the validator produces
// ============================================================

// The canonical `ValidationResult` shape lives in `src/shared/answerContract/`
// so that `content/questions/validators/*` can produce it without depending
// on `practice/`. Re-exported here for backward compatibility — existing
// consumers (`practice/index.ts`, feedback code, etc.) still import it from
// this module.
export type { ValidationResult } from '../../shared/answerContract'

// ============================================================
// CONVERSION UTILITIES
// ============================================================

/**
 * Convert renderer output (string | number) to typed AnswerPayload.
 */
export function toAnswerPayload(
  questionType: QuestionType | string,
  rawAnswer: string | number,
): AnswerPayload {
  const type = questionType || 'multiple_choice'
  switch (type) {
    case 'multiple_choice': {
      // two_step_judge mode emits a JSON-string payload with `step1` key.
      // Detect it before falling back to the legacy choice/index handling.
      if (typeof rawAnswer === 'string' && rawAnswer.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(rawAnswer)
          if (
            parsed &&
            typeof parsed === 'object' &&
            typeof parsed.step1 === 'number'
          ) {
            const out: TwoStepChoicePayload = {
              kind: 'two_step_choice',
              step1: parsed.step1,
            }
            if (typeof parsed.step2 === 'number') {
              out.step2 = parsed.step2
            }
            return out
          }
        } catch {
          /* fall through to default choice handling */
        }
      }
      return {
        kind: 'choice',
        selectedIndex: Number(rawAnswer),
      }
    }
    case 'fill_in':
    case 'cijferend_rekenen':
    case 'cijferend_delen':
    case 'thermometer_slider':
    case 'estimation_slider':
      return {
        kind: 'text',
        value: String(rawAnswer),
      }

    // CANONICAL: Clean clock type (MeesterJay 4.1)
    case 'clock': {
      // Parse HH:MM format
      const timeStr = String(rawAnswer)
      const [hoursStr, minutesStr] = timeStr.split(':')
      const hours = parseInt(hoursStr, 10) || 0
      const minutes = parseInt(minutesStr, 10) || 0
      return {
        kind: 'time',
        value: {
          hours,
          minutes,
          format: '24h',
        },
      }
    }

    // CANONICAL: Clean money type (MeesterJay 4.1)
    case 'money': {
      // Parse token set JSON: [{valueCents, count}, ...]
      let tokens: Array<{
        valueCents: number
        count: number
      }> = []
      try {
        tokens = JSON.parse(String(rawAnswer))
      } catch {
        tokens = []
      }
      const totalCents = tokens.reduce(
        (sum, t) => sum + t.valueCents * t.count,
        0,
      )
      return {
        kind: 'token_set',
        value: {
          tokens,
          totalCents,
        },
      }
    }
    case 'ordering': {
      const sequence = String(rawAnswer).split(',').map(Number)
      return {
        kind: 'ordering',
        sequence,
      }
    }
    case 'matching': {
      let pairs: [number, number][] = []
      try {
        pairs = JSON.parse(String(rawAnswer))
      } catch {
        /* empty */
      }
      return {
        kind: 'mapping',
        pairs,
      }
    }
    case 'drag_drop_fill':
    case 'ratio_table':
    case 'multiplication_grid':
    case 'step_solver':
    case 'sentence_parser':
    case 'verb_conjugation_flow': {
      let values: Record<string, string> = {}
      try {
        values = JSON.parse(String(rawAnswer))
      } catch {
        /* empty */
      }
      return {
        kind: 'key_value',
        values,
      }
    }
    case 'table_reading':
    case 'comparison_judge':
      return {
        kind: 'choice',
        selectedIndex: Number(rawAnswer),
      }
    case 'number_sequence': {
      const vals = String(rawAnswer)
        .split(',')
        .map((s) => s.trim())
      return {
        kind: 'value_sequence',
        values: vals,
      }
    }
    case 'grid_shade': {
      let vals: string[] = []
      try {
        vals = JSON.parse(String(rawAnswer)).map(String)
      } catch {
        /* empty */
      }
      return {
        kind: 'value_sequence',
        values: vals,
      }
    }
    case 'categorize':
    case 'true_false_series':
    case 'error_spotting':
    case 'sentence_error_judge': {
      // These types send JSON strings — store as text payload
      return {
        kind: 'text',
        value: String(rawAnswer),
      }
    }
    case 'text_highlight':
      // Multi-select: renderer sends JSON array of segment IDs
      // Single-select: renderer sends a single segment ID string
      try {
        const parsed = JSON.parse(String(rawAnswer))
        if (Array.isArray(parsed)) {
          return {
            kind: 'text',
            value: String(rawAnswer),
          }
        }
      } catch {
        /* single segment ID — not JSON */
      }
      return {
        kind: 'segment',
        segmentId: String(rawAnswer),
      }
    case 'bar_chart':
    case 'line_chart':
    case 'pie_chart':
      return {
        kind: 'visual_data',
        referenceId: `datapoint-${rawAnswer}`,
        rawIndex: Number(rawAnswer),
      }
    case 'schedule_reading': {
      const [row, col] = String(rawAnswer).split('-').map(Number)
      return {
        kind: 'cell_selection',
        row: row || 0,
        col: col || 0,
      }
    }
    default:
      return {
        kind: 'choice',
        selectedIndex: Number(rawAnswer),
      }
  }
}

/**
 * Convert AnswerPayload back to string | number format.
 */
export function fromAnswerPayload(payload: AnswerPayload): string | number {
  switch (payload.kind) {
    case 'choice':
      return payload.selectedIndex
    case 'text':
      return payload.value
    case 'two_step_choice': {
      const obj: { step1: number; step2?: number } = { step1: payload.step1 }
      if (typeof payload.step2 === 'number') obj.step2 = payload.step2
      return JSON.stringify(obj)
    }

    // CANONICAL token_set format
    case 'token_set':
      // Serialize as JSON (canonical format)
      return JSON.stringify(payload.value.tokens)

    // NEW: Time payload
    case 'time': {
      const h = payload.value.hours.toString().padStart(2, '0')
      const m = payload.value.minutes.toString().padStart(2, '0')
      return `${h}:${m}`
    }
    case 'mapping':
      return JSON.stringify(payload.pairs)
    case 'ordering':
      return payload.sequence.join(',')
    case 'cell_selection':
      return `${payload.row}-${payload.col}`
    case 'key_value':
      return JSON.stringify(payload.values)
    case 'segment':
      return payload.segmentId
    case 'value_sequence':
      return payload.values.join(',')
    case 'visual_data':
      return payload.rawIndex ?? payload.referenceId
  }
}

// ============================================================
// PARSING UTILITIES
// ============================================================

/**
 * Safely parse a serialized canonical payload to AnswerPayload.
 * Rejects legacy and invalid formats.
 */
export function parseAnswerPayload(
  payloadStr: string | undefined,
  _questionType: QuestionType | string,
): AnswerPayload | null {
  if (!payloadStr) return null
  try {
    const parsed = JSON.parse(payloadStr)
    const validation = AnswerPayloadSchema.safeParse(parsed)
    if (!validation.success) {
      return null
    }
    return validation.data
  } catch {
    return null
  }
}
